import { useEffect, useState, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";

// Context
import { FeatureProvider } from "./context/FeatureContext";
import useAutoLogout from "./hooks/useAutoLogout";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Suppliers from "./pages/Suppliers";
import Materials from "./pages/Materials";
import Expenses from "./pages/Expenses";
import Team from "./pages/Team";
import UserManagement from "./pages/UserManagement";
import StandardItems from "./pages/StandardItems";
import Settings from "./pages/Settings";
import Inventory from "./pages/Inventory";
import PurchaseOrders from "./pages/PurchaseOrders";
import Help from "./pages/Help";
import InstallationSystems from "./pages/InstallationSystems";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import ServiceCalendar from "./pages/ServiceCalendar";
import CustomerDashboard from "./pages/CustomerDashboard";
import BookService from "./pages/BookService";
import CustomerProfile from "./pages/CustomerProfile";
import CustomerInstallations from "./pages/CustomerInstallations";
import CustomerBooking from "./pages/CustomerBooking";
import Services from "./pages/Services";
import RentalItems from "./pages/RentalItems";
import RentalManagement from "./pages/RentalManagement";
import RentalJobCard from "./pages/RentalJobCard";
import MyRentals from "./pages/MyRentals";
import MyAssignments from "./pages/MyAssignments";
import DataManagement from "./pages/DataManagement";
import JobCard from "./pages/JobCard";
import MyJobCards from "./pages/MyJobCards";
import AddCustomerService from "./pages/AddCustomerService";
import PendingApprovals from "./pages/PendingApprovals";
import Parts from "./pages/Parts";
import PartCategories from "./pages/PartCategories";
import Labour from "./pages/Labour";
import JobCardTemplate from "./pages/JobCardTemplate";
import JobHistory from "./pages/JobHistory";

// Layout
import AppLayout from "./components/layout/AppLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
axios.defaults.withCredentials = true;

// ─── Global 401 interceptor ───────────────────────────────────────────────────
// Fires whenever any axios request receives a 401 (session expired server-side).
// Uses a module-level flag so only one logout notification is shown per session.
let _isHandlingExpiry = false;

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";

    // Ignore 401/429s from auth routes (these handle their own errors) and
    // session-management endpoints to avoid feedback loops.
    const isAuthRoute =
      url.includes("/auth/login") ||
      url.includes("/auth/logout") ||
      url.includes("/auth/me") ||
      url.includes("/auth/refresh-session") ||
      url.includes("/auth/register") ||
      url.includes("/auth/forgot-password") ||
      url.includes("/auth/reset-password") ||
      url.includes("/auth/verify-reset-token");

    // Never trigger a redirect if the browser is already on an auth page.
    // This is the primary guard against infinite reload loops: if the page
    // is already at /login a 401 should never cause another navigation.
    const isOnAuthPage = ["/login", "/register", "/reset-password"].some(
      (p) => window.location.pathname === p || window.location.pathname.startsWith(p + "/")
    );

    if (status === 401 && !isAuthRoute && !isOnAuthPage && !_isHandlingExpiry) {
      _isHandlingExpiry = true;
      toast.warning("Your session has expired. Please log in again.", {
        duration: 6000,
      });
      // Use replace-state navigation instead of window.location.replace to
      // avoid a full page reload (which resets _isHandlingExpiry and can
      // re-trigger the interceptor before the flag is restored).
      setTimeout(() => {
        _isHandlingExpiry = false;
        // Push to history so React Router re-renders without a hard reload
        window.history.replaceState(null, "", "/login");
        window.dispatchEvent(new PopStateEvent("popstate"));
      }, 1500);
    }

    return Promise.reject(error);
  }
);

// ─── AuthenticatedSession ─────────────────────────────────────────────────────
// Thin wrapper around authenticated routes; activates the inactivity auto-logout.
const AuthenticatedSession = ({ children, isAuthenticated }) => {
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    navigate("/login", { replace: true });
  }, [navigate]);

  useAutoLogout(handleLogout, isAuthenticated === true);

  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      }
    };

    checkAuth();
  }, [navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Check role-based access
  // For workers and managers, also allow access if they have RBAC permissions (handled by AppLayout/Backend)
  // The allowedRoles check is mainly for customer vs non-customer separation
  const isNonCustomerRole = ["admin", "manager", "worker"].includes(user?.role);
  const routeAllowsNonCustomers = allowedRoles?.some(r => ["admin", "manager", "worker"].includes(r));
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // If user is worker/manager and route allows any non-customer role, let them through
    // The backend and AppLayout will enforce the specific RBAC permissions
    if (isNonCustomerRole && routeAllowsNonCustomers) {
      // Allow access - RBAC system will handle the actual permission check
    } else {
      // Redirect based on role
      if (user?.role === "customer") {
        return <Navigate to="/customer-dashboard" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  return (
    <AuthenticatedSession isAuthenticated={isAuthenticated}>
      <AppLayout user={user}>{children}</AppLayout>
    </AuthenticatedSession>
  );
};

// App Router
const AppRouter = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Admin/Manager/Worker Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pending-approvals"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <PendingApprovals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <Projects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <ProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <Suppliers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/materials"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <Materials />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <Expenses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <Team />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parts"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <Parts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/part-categories"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]}>
            <PartCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/labour"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <Labour />
          </ProtectedRoute>
        }
      />
      <Route
        path="/standard-items"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <StandardItems />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-card-template"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <JobCardTemplate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/data-management"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <DataManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/add-customer-service"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <AddCustomerService />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <Inventory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-orders"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <PurchaseOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <Help />
          </ProtectedRoute>
        }
      />
      
      {/* Admin - Customer Service Management Routes */}
      <Route
        path="/installation-systems"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <InstallationSystems />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Customers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:customerId"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <CustomerDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-calendar"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ServiceCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/services"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Services />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rental-items"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <RentalItems />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rental-management"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <RentalManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rental-job-card/:rentalId"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker", "customer"]}>
            <RentalJobCard />
          </ProtectedRoute>
        }
      />
      
      {/* Worker Routes */}
      <Route
        path="/my-assignments"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <MyAssignments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-history"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker"]}>
            <JobHistory />
          </ProtectedRoute>
        }
      />
      
      {/* Customer Portal Routes */}
      <Route
        path="/customer-dashboard"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <CustomerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/book-service"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <BookService />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-profile"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <CustomerProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-installations"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <CustomerInstallations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-bookings"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <CustomerBooking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer/rentals"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <MyRentals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-job-cards"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <MyJobCards />
          </ProtectedRoute>
        }
      />
      
      {/* Job Card Route - accessible by admin, manager, worker, and customer */}
      <Route
        path="/job-card/:bookingId"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "worker", "customer"]}>
            <JobCard />
          </ProtectedRoute>
        }
      />
      
      {/* Default redirect based on role - handled by SmartRedirect */}
      <Route path="/" element={<SmartRedirect />} />
      <Route path="*" element={<SmartRedirect />} />
    </Routes>
  );
};

// Smart redirect based on user role
const SmartRedirect = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAndRedirect = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        const user = response.data;
        if (user.role === "customer") {
          navigate("/customer-dashboard", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } catch (error) {
        navigate("/login", { replace: true });
      }
      setLoading(false);
    };
    checkAndRedirect();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  return null;
};

function App() {
  return (
    <BrowserRouter>
      <FeatureProvider>
        <AppRouter />
        <Toaster position="top-right" />
      </FeatureProvider>
    </BrowserRouter>
  );
}

export default App;
